import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [ "primary", "secondary", "tertiary", "output", "previewTitle", "previewDescription", "grid" ]
  static values = { kind: String }

  connect() {
    this.scenario = 0
    this.analyze()
  }

  analyze() {
    this.outputTarget.textContent = this[this.kindValue]()
  }

  snippet() {
    this.previewTitleTarget.textContent = this.primaryTarget.value
    this.previewDescriptionTarget.textContent = this.tertiaryTarget.value
    return `${this.primaryTarget.value.length} title characters · ${this.tertiaryTarget.value.length} description characters · preview only; search engines may rewrite either value.`
  }

  title() {
    const count = this.primaryTarget.value.trim().length
    return `${count} characters. ${count < 30 ? "This is unusually short; confirm it communicates the page purpose." : count > 65 ? "This may be clipped; move the most specific language earlier." : "This is within a common review range, but relevance and uniqueness still require judgment."}`
  }

  description() {
    const count = this.primaryTarget.value.trim().length
    return `${count} characters. ${count < 70 ? "The description may not communicate enough specific value." : count > 170 ? "The description is likely to be clipped in many presentations." : "The length is reasonable; confirm the copy is page-specific and accurate."}`
  }

  canonical() {
    try {
      const page = new URL(this.primaryTarget.value)
      const canonical = new URL(this.secondaryTarget.value)
      if (canonical.hash) return "The canonical contains a fragment. Review and remove the fragment before using it as a consolidation target."
      if (page.hostname.replace(/^www\./, "") !== canonical.hostname.replace(/^www\./, "")) return `Cross-domain target: ${canonical.hostname}. Confirm ownership and consolidation intent before publishing.`
      return page.href === canonical.href ? "The canonical is an exact self-reference. Confirm that one canonical link appears inside the live document head." : "The URLs are valid and share an apex host, but protocol, www, path, query, and redirect intent still need review."
    } catch (_error) {
      return "One or both values are not valid absolute HTTP(S) URLs."
    }
  }

  redirects() {
    const urls = this.primaryTarget.value.split(/\n/).map((value) => value.trim()).filter(Boolean)
    const duplicates = urls.filter((url, index) => urls.indexOf(url) !== index)
    return duplicates.length > 0 ? `Likely loop: ${[ ...new Set(duplicates) ].join(", ")} appears more than once.` : `${Math.max(urls.length - 1, 0)} hop(s) entered. Verify each live status and replace avoidable internal links to intermediate URLs.`
  }

  robots() {
    const path = this.secondaryTarget.value.trim()
    const lines = this.primaryTarget.value.split(/\n/).map((line) => line.trim())
    const rules = lines.filter((line) => /^(allow|disallow):/i.test(line)).map((line) => line.split(":").slice(1).join(":").trim()).filter((rule) => path.startsWith(rule))
    return rules.length === 0 ? `No pasted Allow or Disallow prefix matches ${path || "/"}. Confirm the applicable user-agent group.` : `${rules.length} pasted rule(s) match ${path}. The longest applicable rule is usually the starting point; confirm behavior with the target crawler.`
  }

  sitemap() {
    const xmlDocument = new DOMParser().parseFromString(this.primaryTarget.value, "application/xml")
    if (xmlDocument.querySelector("parsererror")) return "The XML could not be parsed. Fix the syntax before reviewing sitemap semantics."
    const locations = [ ...xmlDocument.querySelectorAll("loc") ].map((node) => node.textContent.trim())
    const duplicates = locations.length - new Set(locations).size
    return `${xmlDocument.documentElement.localName} parsed with ${locations.length} location(s) and ${duplicates} duplicate location value(s). Live status, indexability, and canonical agreement are not checked here.`
  }

  estimate() {
    const sites = Number(this.primaryTarget.value) || 0
    const pages = Number(this.secondaryTarget.value) || 0
    const minutes = Number(this.tertiaryTarget.value) || 0
    const hours = (sites * pages / 100 * minutes / 60).toFixed(1)
    return `${(sites * pages).toLocaleString()} estimated pages · about ${hours} expert review hours under these assumptions, before implementation, QA, and reporting.`
  }

  grid() {
    this.scenario += 1
    const cells = [ ...this.gridTarget.querySelectorAll("span") ]
    cells.forEach((cell, index) => {
      const rank = ((index * 3 + this.scenario * 4) % 20) + 1
      cell.textContent = rank
      cell.dataset.rank = rank <= 5 ? "high" : rank <= 12 ? "mid" : "low"
    })
    return `Synthetic scenario ${this.scenario}: inspect the point pattern rather than treating the colors as live Google data.`
  }

  report() {
    return "This synthetic card shows three fictional summary counts. It does not load representative work or demonstrate real report access."
  }
}
